import { ThemeEvents, CartAddEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';

/**
 * Product add-on pickers ("Add a card" / "Add a vase" / "Add gifts").
 *
 * Each <product-addons> element wraps a list of selectable add-on tiles
 * (radio for single-select message cards, checkbox for multi-select gifts).
 * Selection alone never touches the cart.
 *
 * Adding to cart is ONE request: a window-level capture listener intercepts
 * the main product form's submit before the theme's delegated handler
 * (component.js delegates on:submit at the document level, also in capture
 * phase — window capture fires first) and POSTs the main product plus every
 * selected add-on in a single /cart/add call. The add-to-cart button shows a
 * loading spinner until the cart has actually updated, and the "Added"
 * checkmark plays only after the server confirmed (data-puppet suppresses the
 * stock optimistic click animation).
 *
 * When no add-on is selected the stock single-item flow runs unchanged with
 * the same loading state mirrored around it. The old cart:update fallback
 * (batch-add after the main add) remains for the rare stock paths that are
 * not intercepted, e.g. an add queued while a variant change is in flight.
 *
 * Single-select pickers with a data-property-name also mirror the selected
 * add-on's title into a hidden `properties[...]` input inside the main
 * product form, so the main line item records which card was chosen
 * (legacy `properties[Card]` behaviour from the old theme).
 *
 * IMMEDIATE pickers (data-addon-immediate, used by the gifts AND message-card
 * sections) are the exception: selecting a tile adds that item to the cart on
 * the spot and deselecting removes it, instead of waiting for the main form
 * submit. Single-select immediate pickers (radio cards) also remove the
 * previously-added line when the selection changes. They are excluded from the
 * combined Send Blooms add so they're never double-added. The
 * added line's key is stored on the input (data-cart-line-key, also rendered
 * server-side for items already in the cart) so a deselect removes the exact
 * line. Each immediate add/remove refreshes the cart icon and pops a toast but
 * never opens the cart drawer.
 */

/**
 * Finds the main on-page product form for a product, skipping forms inside
 * quick-add dialogs and product cards.
 * @param {string} productId
 * @returns {HTMLElement | null}
 */
function findMainProductForm(productId) {
  const candidates = document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`);
  for (const candidate of candidates) {
    if (!candidate.closest('dialog, product-card, .quick-add-modal')) return candidate;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Bundle identity
// ---------------------------------------------------------------------------

/**
 * Every add-to-cart of a bouquet stamps a unique `properties[_bundle]` id on
 * the main line AND on each of its companion lines (card, hatbox). Without it
 * Shopify merges lines that share a variant + identical properties, so adding
 * the same bouquet twice — even with different cards/colours — collapsed into
 * one line at a higher quantity and its companions merged too, stapling the
 * wrong card/hatbox onto every matching parent (the "same item became the same
 * item" bug). A per-add id makes each bouquet its own line that keeps its own
 * companions; cart-companion-addons.js and the V1 cart group companions to
 * their parent by this id (falling back to the legacy product-id link for
 * carts created before this shipped).
 *
 * The id lives as a hidden `properties[_bundle]` input inside the main product
 * form so BOTH the combined add (product-addons single request) and the stock
 * single-item flow serialize it onto the main line. Companion adds read the
 * current value off that input. `_`-prefixed => hidden from the customer and
 * from fulfilment, but still part of the line's merge signature.
 */

/**
 * Is the grouped cart (V2) the active design? Read from the theme setting, which
 * is global precisely so this file can see it: the stamping decision happens on
 * the PRODUCT page, where a cart-section setting is not visible.
 *
 * Under V1 the stamping stays exactly as it was — independent add-ons unstamped,
 * so the live cart keeps showing them as their own customer-managed lines and
 * never cascades them.
 * @returns {boolean}
 */
function groupedCartActive() {
  // Bare `Theme`, not globalThis.Theme: snippets/scripts.liquid declares it with
  // `const` at classic-script top level, which lands in the global LEXICAL scope
  // and is therefore readable by identifier but absent from globalThis. Reading
  // it off globalThis silently returned undefined, so the gate never opened.
  return typeof Theme !== 'undefined' && Theme?.settings?.cartVersion === 'v2';
}

/** @returns {string} */
function newBundleId() {
  const rand =
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    Date.now().toString(36) + Math.random().toString(36).slice(2);
  return `b-${rand}`;
}

/**
 * Returns the main form's hidden bundle-id input, creating it (with a fresh id)
 * when `create` is set. Null when the product has no on-page main form.
 * @param {string} productId
 * @param {boolean} [create]
 * @returns {HTMLInputElement | null}
 */
function bundleInput(productId, create = false) {
  const form = findMainProductForm(productId)?.querySelector('form');
  if (!form) return null;
  let input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[data-bundle-id]'));
  if (!input && create) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[_bundle]';
    input.dataset.bundleId = 'true';
    input.value = newBundleId();
    form.appendChild(input);
  }
  return input;
}

/**
 * The current bundle id for a product, creating the input on first use so the
 * main line and its companions always share one id.
 * @param {string} productId
 * @returns {string}
 */
function currentBundleId(productId) {
  return bundleInput(productId, true)?.value || '';
}

/**
 * Starts a NEW bundle: the just-added bouquet keeps the id it was stamped with,
 * and the next add (a second bouquet configured on the same page, or the same
 * PDP restored from bfcache) gets a fresh one so the two never merge.
 * @param {string} productId
 */
function rotateBundleId(productId) {
  const input = bundleInput(productId, false);
  if (input) input.value = newBundleId();
}

// ---------------------------------------------------------------------------
// Variant filter matching (size -> colour tiles)
// ---------------------------------------------------------------------------

/**
 * Scores how well a tile's title matches the selected option value using
 * whole-word-run segment matching, so "Large" never matches "Extra Large"
 * on a plain substring test. Mirrors the Liquid render in
 * blocks/addon-variant-picker.liquid so the server-rendered initial state and
 * this client-side re-filter always agree.
 *
 * The tile title is split into segments on -, / and |. A segment matches
 * when it appears in the selected value as a whole word run, tested with the
 * space-padding trick (`" large hatbox "` contains `" large "` but not
 * `" extra large "`). Callers keep only the tiles whose best segment match
 * is longest (more words = more specific), so "Extra Large Hatbox" prefers
 * "extra large" tiles over plain "large" ones.
 * @param {string} paddedValue - the selected value, lowercased, whitespace-collapsed, and padded with a leading/trailing space (e.g. " large hatbox ")
 * @param {string} text - the tile's raw title / data-match-text
 * @returns {number} word count of the best-matching segment, or 0 if none match
 */
function segmentMatchLength(paddedValue, text) {
  if (!paddedValue || !text) return 0;

  const segments = text
    .toLowerCase()
    .split(/[-/|]/)
    .map((segment) => segment.trim().replace(/\s+/g, ' '))
    .filter(Boolean);

  let best = 0;
  for (const segment of segments) {
    if (!paddedValue.includes(` ${segment} `)) continue;
    const words = segment.split(' ').length;
    if (words > best) best = words;
  }
  return best;
}

/**
 * Which tiles the selected option value shows, one flag per tile: the tiles
 * tied for the best segmentMatchLength; when none scores, the old substring
 * test on the full value, then on its first word ("Petit Hatbox" -> "Petit");
 * when even that misses, every tile rather than an empty step. Mirrors the
 * Liquid render in blocks/addon-variant-picker.liquid.
 * @param {string[]} texts - each tile's data-match-text
 * @param {string} value - the selected option value
 * @returns {boolean[]}
 */
function variantFilterMatches(texts, value) {
  let normalizedValue = value.toLowerCase().trim().replace(/\s+/g, ' ');
  // A free-upgrade size ships at the size it upgrades TO, so match on that part:
  // "Standard Hatbox with Free Upgrade to Large" -> large tiles only, not standard too.
  const upgradeAt = normalizedValue.lastIndexOf('upgrade to ');
  if (upgradeAt !== -1) normalizedValue = normalizedValue.slice(upgradeAt + 'upgrade to '.length);
  const paddedValue = ` ${normalizedValue} `;

  const scores = texts.map((text) => segmentMatchLength(paddedValue, text));
  const bestScore = Math.max(0, ...scores);

  let matches;
  if (bestScore > 0) {
    matches = scores.map((score) => score === bestScore);
  } else {
    let lower = normalizedValue;
    if (!texts.some((text) => text.toLowerCase().includes(lower))) {
      lower = normalizedValue.split(' ')[0] || '';
    }
    matches = texts.map((text) => lower !== '' && text.toLowerCase().includes(lower));
  }

  // Ultimate fallback: if literally nothing matched (new rule AND the old
  // substring/first-word rule both miss, e.g. a size with no tiles at
  // all), show every tile rather than an empty step.
  if (!matches.some(Boolean)) matches = texts.map(() => true);
  return matches;
}

/**
 * A title's distinct lowercase words: "STANDARD – Nude (Premium)" -> [standard, nude, premium].
 * @param {string} title
 * @returns {string[]}
 */
function titleWords(title) {
  return String(title || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word, index, words) => word !== '' && words.indexOf(word) === index);
}

/**
 * Which in-stock tile of the new size is the same colour as the chosen one, as
 * an index into candidateTitles, or -1 when the new size does not have that
 * colour in stock -- the caller then takes the very first in-stock tile.
 *
 * Read from the tile titles, with no fixed format and no colour list. Size
 * words are recognised by where they appear across every tile of each size,
 * sold out included:
 *   - the chosen tile's size words are shared by every tile of its own size and
 *     appear in no tile of the new size ("standard");
 *   - the new size's words are shared by every tile of the new size and are not
 *     in the chosen title ("large").
 * What remains of the chosen title is its colour. Only a tile whose remaining
 * words are EXACTLY that colour counts, so "Gold" never becomes "Rose Gold",
 * "Rose Gold" never becomes "Gold", and anything in doubt falls back to the
 * very first tile.
 *
 * Limitation: size words are only known from the titles, so a colour word that
 * every tile of a size shares (a size offering only "Rose Gold" and "Gold", or
 * a single tile) is read as a size word, and a look-alike colour can then be
 * kept. Not reachable with distinct one-word colours such as White / Pink.
 * @param {string} chosenTitle
 * @param {string[]} ownTitles - every tile of the chosen tile's size
 * @param {string[]} newTitles - every tile of the new size
 * @param {string[]} candidateTitles - the new size's in-stock tiles
 * @returns {number}
 */
function sameColourIndex(chosenTitle, ownTitles, newTitles, candidateTitles) {
  const chosen = titleWords(chosenTitle);
  const own = ownTitles.map(titleWords);
  const fresh = newTitles.map(titleWords);
  /** @param {string[][]} lists @param {string} word */
  const inAll = (lists, word) => lists.length > 0 && lists.every((words) => words.includes(word));
  /** @param {string[][]} lists @param {string} word */
  const inAny = (lists, word) => lists.some((words) => words.includes(word));
  const colour = chosen.filter((word) => !(inAll(own, word) && !inAny(fresh, word)));

  if (!colour.length) return -1;

  return candidateTitles.findIndex((title) => {
    const words = titleWords(title).filter((word) => !(inAll(fresh, word) && !chosen.includes(word)));
    return words.length === colour.length && colour.every((word) => words.includes(word));
  });
}

// ---------------------------------------------------------------------------
// Main photo follows the chosen tile (hatbox size + colour)
// ---------------------------------------------------------------------------

/*
 * Which gallery photo belongs to which choice comes from the gallery FILENAME,
 * never from gallery order -- see the "-hb-" tag in
 * snippets/product-media-gallery-content.liquid, the naming the marketing team
 * uses across brands:
 *
 *     <anything>-hb-<colour>          product without the filter (size) option
 *     <anything>-hb-<size>-<colour>   size product
 *
 * Nothing here knows a colour or a size name, and the tile title needs no fixed
 * format: the photo's colour only has to appear as words in the chosen tile's
 * title, and its size has to be how the size value starts. Colours and sizes
 * can be added, removed or renamed in Shopify with no code change. A choice
 * without a matching photo leaves the gallery exactly as the theme sets it.
 */

const PHOTO_SETTLE_MS = 550; // pressed feedback on a newly chosen tile
const PHOTO_HOLD_MS = 700; // how long a change keeps correcting the gallery
const PHOTO_HOLD_MAX_MS = 2500; // ...or longer while the photo is still wrong, up to this
const PHOTO_LOADING_MS = 6000; // the gallery spinner can never stick
const VARIANT_WAIT_MS = 10000; // how late the variant's response may still land
const PHOTO_SETTLING_CLASS = 'addon-choice-settling';
const PHOTO_LOADING_CLASS = 'addon-photo-loading';

/** The one photo hold on the page (see #holdChosenPhoto). @type {AbortController | null} */
let activePhotoHold = null;

/**
 * "Standard with Free Crispy Hearts" -> "standard-with-free-crispy-hearts".
 * @param {unknown} value
 * @returns {string}
 */
function slugify(value) {
  return (value == null ? '' : String(value))
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Whole-word containment on slugs: "nude" is in "xl-nude-premium", "blue" is not in "blueberry".
 * @param {string} slug
 * @param {string} words
 * @returns {boolean}
 */
function hasWords(slug, words) {
  return words !== '' && `-${slug}-`.includes(`-${words}-`);
}

/**
 * A photo's colour belongs to the tile of the same size whose title is closest
 * to it, so a "-gold" photo is Gold's, not Rose Gold's -- Rose Gold waits for
 * its own photo. sizeBoxes is every tile of the chosen tile's size, sold out
 * included, so the owner does not change with stock. A tie includes the chosen tile.
 * @param {string} title - slug
 * @param {string} colour - slug
 * @param {string[]} sizeBoxes - slugs
 * @returns {boolean}
 */
function ownsColour(title, colour, sizeBoxes) {
  const colourWords = colour.split('-').length;
  /** @param {string} slug */
  const extraWords = (slug) => slug.split('-').length - colourWords;
  const mine = extraWords(title);
  return sizeBoxes.every((other) => !hasWords(other, colour) || extraWords(other) >= mine);
}

/**
 * The photo for the chosen tile, as an index into tags, or -1:
 *   - size product: the tag must split as "<size>-<colour>" where the size value
 *     STARTS WITH <size> ("Standard with Free Crispy Hearts" matches "standard")
 *     and the tile title contains <colour> as whole words. It never falls back
 *     to a colour-only photo, which would misrepresent the size being bought.
 *   - no size option: the tile title contains the whole tag as words.
 * Either way the colour must belong to the chosen tile (ownsColour). The
 * longest size wins ("extra-large" over "large"), then the longest colour
 * ("light-blue" over "blue"); a tie keeps gallery order.
 * @param {string[]} tags - each tagged photo's data-addon-photo, in gallery order
 * @param {string} title - the chosen tile's title, slugified
 * @param {string | null} size - the chosen size value, slugified; null without the size option
 * @param {string[]} sizeBoxes - every tile of the chosen tile's size, slugified
 * @returns {number}
 */
function photoFor(tags, title, size, sizeBoxes) {
  let best = -1;
  let bestSize = -1;
  let bestColour = -1;
  /** @param {string} colour */
  const fits = (colour) => hasWords(title, colour) && ownsColour(title, colour, sizeBoxes);

  tags.forEach((tag, index) => {
    if (size === null) {
      if (tag.length > bestColour && fits(tag)) {
        best = index;
        bestSize = 0;
        bestColour = tag.length;
      }
      return;
    }

    for (let cut = tag.indexOf('-'); cut > 0; cut = tag.indexOf('-', cut + 1)) {
      const tagSize = tag.slice(0, cut);
      const tagColour = tag.slice(cut + 1);
      const sizeMatches = size === tagSize || size.startsWith(`${tagSize}-`);

      if (
        sizeMatches &&
        fits(tagColour) &&
        (tagSize.length > bestSize || (tagSize.length === bestSize && tagColour.length > bestColour))
      ) {
        best = index;
        bestSize = tagSize.length;
        bestColour = tagColour.length;
      }
    }
  });

  return best;
}

/**
 * Spinner on a gallery slide whose photo is still downloading, so a photo
 * that is already there never flickers. Only the first gallery image loads
 * eagerly; one the gallery jumps to was never near the viewport, so its
 * download starts here too (the same eager upgrade the tile images use).
 * @param {HTMLElement} slide
 */
function showPhotoLoading(slide) {
  const img = slide.querySelector('img');
  if (!(img instanceof HTMLImageElement)) return;
  if (img.loading === 'lazy') img.loading = 'eager';
  // complete: loaded, or broken (nothing left to wait for). A lazy image that
  // has not started downloading is not complete.
  if (img.complete || slide.classList.contains(PHOTO_LOADING_CLASS)) return;

  let timer = 0;
  const done = () => {
    clearTimeout(timer);
    img.removeEventListener('load', done);
    img.removeEventListener('error', done);
    slide.classList.remove(PHOTO_LOADING_CLASS);
  };
  slide.classList.add(PHOTO_LOADING_CLASS);
  img.addEventListener('load', done);
  img.addEventListener('error', done);
  timer = setTimeout(done, PHOTO_LOADING_MS);
}

class ProductAddons extends HTMLElement {
  #abortController = new AbortController();

  /** Which tiles the last filter pass showed: the chosen tile's size. @type {boolean[] | null} */
  #appliedMatches = null;

  /** The option value the tiles were last filtered by (the chosen size). */
  #matchValue = '';

  /** When the shopper last changed the main product's variant (ms); 0 once used. */
  #variantChangedAt = 0;

  /** @type {AbortController | null} */
  #photoHold = null;

  #photoHoldTimer = 0;

  #settleTimer = 0;

  /** @type {Element | null} */
  #settlingCard = null;

  #photosWarmed = false;

  connectedCallback() {
    // Fresh per connection: disconnectedCallback aborts it, and a picker that
    // is moved in the DOM reconnects and must get working listeners again.
    this.#abortController = new AbortController();
    this.addEventListener('change', this.#onChange);
    this.#initImageSkeletons();

    // Pickers whose options follow one of the main product's options (e.g.
    // hatbox colours filtered by the selected Size) re-filter on every
    // variant change. data-match-option-position holds the option position
    // (1-3) of the main product option to match variant titles against.
    // Photo-following pickers listen too: a variant change replaces the gallery.
    if (this.dataset.matchOptionPosition || this.#followsPhoto) {
      document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
        signal: this.#abortController.signal,
      });
    }
    if (this.dataset.matchOptionPosition && this.dataset.matchValue) {
      this.#applyVariantFilter(this.dataset.matchValue);
    }
    if (this.#followsPhoto) this.#initPhotoFollow();

    // Single-select pickers preselect the free option server-side, so the
    // line item property needs to exist before the first add to cart.
    if (this.dataset.propertyName) this.#syncLineItemProperty();

    // Make sure the main form carries a bundle id from the start, so even a
    // bare bouquet added through the stock flow (no add-ons selected) gets its
    // own line and any immediate companion add can read the shared id.
    if (this.dataset.productId) bundleInput(this.dataset.productId, true);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#abortController.abort();
    this.#photoHold?.abort();
    this.#finishSettling();
  }

  /**
   * Drives the tile image skeleton (snippets/addon-product-card.liquid). The
   * media square is reserved up-front, so a lazy-loaded image leaves a blank
   * box until it lands — the skeleton shimmer fills it and is removed here by
   * adding .is-loaded the moment each image's load (or error) fires.
   *
   * `load`/`error` don't bubble but DO fire in the capture phase on ancestors,
   * so one capture listener on the picker catches every descendant tile image —
   * including lazy images that load later and carousel slides cloned after
   * connect. Images already cached (load fired before this ran) are swept once.
   *
   * Carousel-only eager upgrade: tiles are marked loading="lazy" in
   * snippets/addon-product-card.liquid (right for grids — every tile is in
   * normal flow). But in a carousel the off-page/peeking tiles are scrolled out
   * of (or clipped by) the slideshow viewport, so native lazy loading never
   * fetches them and their skeleton hangs forever. When this picker is a
   * carousel (a <slideshow-component> is present) we upgrade the lazy tile
   * images to eager so the browser kicks the deferred loads. Grids have no
   * slideshow-component, so they stay lazy and avoid wasted up-front fetches.
   */
  #initImageSkeletons() {
    this.addEventListener('load', this.#onImageSettled, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('error', this.#onImageSettled, {
      capture: true,
      signal: this.#abortController.signal,
    });
    const isCarousel = this.querySelector('slideshow-component') != null;
    // Filtered pickers (hatbox colours: data-match-option-position) show only
    // the two or three tiles matching the chosen size and hide the rest. Left
    // lazy, those few visible tiles sit as empty skeletons while the card and
    // gift carousels next to them — already upgraded by the branch above —
    // show their pictures straight away. That asymmetry is what reads as the
    // colour step being broken. Upgrade them too, but only the tiles actually
    // on show: an eager image is fetched even inside a display:none wrapper,
    // so upgrading all eight would pull five images nobody is looking at.
    // Tiles revealed later by a size change are handled in #applyVariantFilter.
    const isFilteredPicker = this.dataset.matchOptionPosition != null;
    for (const img of this.querySelectorAll('.addon-card__media img')) {
      if (!(img instanceof HTMLImageElement)) continue;
      if (img.loading === 'lazy') {
        const wrapper = img.closest('.resource-list__item, .resource-list__slide');
        const isOnShow = wrapper == null || !wrapper.hasAttribute('hidden');
        if (isCarousel || (isFilteredPicker && isOnShow)) img.loading = 'eager';
      }
      if (img.complete && img.naturalWidth > 0) {
        img.closest('.addon-card__media')?.classList.add('is-loaded');
      }
    }
  }

  /** @param {Event} event */
  #onImageSettled = (event) => {
    const img = event.target;
    if (img instanceof HTMLImageElement) img.closest('.addon-card__media')?.classList.add('is-loaded');
  };

  /** @param {Event & {detail?: any}} event */
  #onVariantUpdate = (event) => {
    const data = event.detail?.data;
    if (!data || String(data.productId) !== this.dataset.productId) return;

    const variant = event.detail.resource;
    if (!variant) return;

    if (this.dataset.matchOptionPosition) {
      const value = variant[`option${this.dataset.matchOptionPosition}`];
      if (typeof value === 'string' && value) this.#applyVariantFilter(value);
    }

    // A variant change the shopper made: the gallery has just been replaced
    // (media-gallery.js listens on the section, so it runs before this
    // document listener) with one opening on the variant's own image. Put the
    // chosen size + tile photo back. Once per change, and never for a response
    // later than VARIANT_WAIT_MS, so a stale or programmatic update never pulls
    // the gallery.
    if (this.#variantChangedAt && Date.now() - this.#variantChangedAt <= VARIANT_WAIT_MS) {
      this.#variantChangedAt = 0;
      this.#holdChosenPhoto();
    }
  };

  /**
   * Shows only the option tiles that best match the given value using
   * whole-word-run segment matching, longest match wins (e.g. Size
   * "Large Hatbox" -> only "Large - White" / "Large - Pink", never
   * "Extra Large - ..."; "Extra Large Hatbox" -> only the "Extra Large - ..."
   * tiles, since "extra large" out-scores "large"). See segmentMatchLength.
   * When nothing matches under that rule, falls back to the old substring
   * test (and its first-word reduction, e.g. "Petit Hatbox" -> "Petit"); if
   * even that matches nothing, shows every tile rather than an empty step.
   * Mirrors the Liquid render fallback in blocks/addon-variant-picker.liquid.
   * A selection that became hidden moves to the same colour in the new size
   * when that is in stock (sameColourIndex), else is unchecked; when
   * data-addon-required is set, the first visible in-stock tile is then
   * auto-selected (old hatbox-options behaviour).
   * @param {string} value
   */
  #applyVariantFilter(value) {
    const options = Array.from(this.querySelectorAll('[data-match-text]'));
    const texts = options.map((option) =>
      option instanceof HTMLElement ? option.dataset.matchText || '' : ''
    );
    const matches = variantFilterMatches(texts, value);
    const keepIndex = this.#sameColourTile(options, texts, matches);

    this.#appliedMatches = matches;
    this.#matchValue = value;

    let hasVisibleChecked = false;

    options.forEach((option, index) => {
      if (!(option instanceof HTMLElement)) return;
      const isMatch = matches[index];
      const wrapper = option.closest('.resource-list__item, .resource-list__slide') || option;
      const wasHidden = wrapper.hasAttribute('hidden');
      wrapper.toggleAttribute('hidden', !isMatch);

      // A tile that was display:none has no box, so native lazy loading never
      // fetched its image. Revealing it does not reliably restart that fetch,
      // which left the newly shown colour tiles as shimmering grey squares
      // after a size change. Same eager upgrade the carousel branch of
      // #initImageSkeletons uses, applied only to tiles we just revealed.
      if (isMatch && wasHidden) {
        const revealedImage = option.querySelector('.addon-card__media img');
        if (
          revealedImage instanceof HTMLImageElement &&
          revealedImage.loading === 'lazy' &&
          !revealedImage.complete
        ) {
          revealedImage.loading = 'eager';
        }
      }

      const input = option.querySelector('input.addon-card__input');
      if (!(input instanceof HTMLInputElement)) return;
      if (!isMatch && input.checked) input.checked = false;
      if (index === keepIndex) input.checked = true;
      if (isMatch && input.checked) hasVisibleChecked = true;
    });

    if (!hasVisibleChecked && this.dataset.addonRequired === 'true') {
      for (const option of options) {
        if (!(option instanceof HTMLElement)) continue;
        const wrapper = option.closest('.resource-list__item, .resource-list__slide') || option;
        if (wrapper.hasAttribute('hidden')) continue;
        const input = option.querySelector('input.addon-card__input:not(:disabled)');
        if (input instanceof HTMLInputElement) {
          input.checked = true;
          break;
        }
      }
    }

    if (this.dataset.propertyName) this.#syncLineItemProperty();

    // Carousel mode: hidden slides collapse, so reset the scroller to the start
    // and tell the page-dots to recount the now-visible slides.
    const slideshow = this.querySelector('slideshow-component');
    if (slideshow) {
      slideshow.querySelector('slideshow-slides')?.scrollTo({ left: 0 });
      slideshow.dispatchEvent(new CustomEvent('addon-carousel:refresh'));
    }
  }

  /**
   * On a size change, the tile of the new size to keep the chosen colour on:
   * only an exact colour match that is in stock (sold-out tiles are disabled),
   * else -1 and the required fallback takes the very first in-stock tile. -1
   * too when the chosen tile stays visible, or on the first filter pass.
   * @param {Element[]} options - every tile, in order
   * @param {string[]} texts - their titles
   * @param {boolean[]} matches - the tiles the new value shows
   * @returns {number}
   */
  #sameColourTile(options, texts, matches) {
    const previous = this.#appliedMatches;
    if (!previous) return -1;

    const inputs = options.map((option) => option.querySelector('input.addon-card__input'));
    const chosen = inputs.findIndex((input) => input instanceof HTMLInputElement && input.checked);
    if (chosen < 0 || matches[chosen]) return -1;

    const candidates = texts
      .map((_, index) => index)
      .filter((index) => {
        const input = inputs[index];
        return matches[index] && input instanceof HTMLInputElement && !input.disabled;
      });
    const keep = sameColourIndex(
      texts[chosen],
      texts.filter((_, index) => previous[index]),
      texts.filter((_, index) => matches[index]),
      candidates.map((index) => texts[index])
    );
    return keep > -1 ? candidates[keep] : -1;
  }

  get #followsPhoto() {
    return this.dataset.photoFollows === 'true';
  }

  /**
   * Main photo follows the chosen tile. Only real shopper input moves the
   * gallery: a tile pick (#onChange) and a variant change armed here. The
   * picker's own auto-select and variant-auto-select.js's load-time switch
   * are programmatic, so nothing moves on page load.
   */
  #initPhotoFollow() {
    const { signal } = this.#abortController;

    // Capture, so it is armed before the variant picker starts its fetch;
    // isTrusted, so variant-auto-select.js's synthetic change never arms it.
    // Size changes only: another option's own variant image must not be
    // covered by the hatbox photo. Every picker style (steps, buttons,
    // dropdowns) renders one .variant-option per product option, in option
    // order, so the changed control's container gives its position. Without
    // the filter option nothing is armed, as on FIE.
    document.addEventListener(
      'change',
      (event) => {
        const picker = this.#mainVariantPicker();
        const target = event.target;
        if (!event.isTrusted || !picker || !(target instanceof HTMLElement) || !picker.contains(target)) return;
        const position = Number(this.dataset.matchOptionPosition);
        const option = target.closest('.variant-option');
        const index = option ? Array.from(picker.querySelectorAll('.variant-option')).indexOf(option) : -1;
        if (position > 0 && index === position - 1) this.#variantChangedAt = Date.now();
      },
      { capture: true, signal }
    );

    // Warm the tagged photos once the shopper shows intent, not on load:
    // choosing is then usually instant, LCP is untouched, and a shopper who
    // never touches the choices downloads nothing extra.
    const warm = () => this.#warmPhotos();
    for (const element of [this, this.#mainVariantPicker()]) {
      if (!element) continue;
      for (const type of ['pointerenter', 'touchstart', 'focusin']) {
        element.addEventListener(type, warm, { once: true, passive: true, signal });
      }
    }
  }

  /** @returns {HTMLElement | null} */
  #mainVariantPicker() {
    for (const picker of document.querySelectorAll(`variant-picker[data-product-id="${this.dataset.productId}"]`)) {
      if (picker instanceof HTMLElement && !picker.closest('dialog, product-card, .quick-add-modal')) return picker;
    }
    return null;
  }

  /**
   * The main product gallery's slideshow, looked up every time because a
   * variant change replaces the whole gallery.
   * @returns {import('./slideshow').Slideshow | null}
   */
  #gallerySlideshow() {
    const scope = this.closest('.shopify-section') || document;
    for (const gallery of scope.querySelectorAll('media-gallery')) {
      if (gallery.closest('dialog, product-card, .quick-add-modal')) continue;
      const slideshow = gallery.querySelector('slideshow-component[ref="slideshow"]');
      if (slideshow) return /** @type {import('./slideshow').Slideshow} */ (slideshow);
    }
    return null;
  }

  #warmPhotos() {
    if (this.#photosWarmed) return;
    this.#photosWarmed = true;

    const connection = /** @type {any} */ (navigator).connection || {};
    if (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')) return;

    const run = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
    run(() => {
      for (const img of this.#gallerySlideshow()?.querySelectorAll('slideshow-slide[data-addon-photo] img') ?? []) {
        if (img instanceof HTMLImageElement && img.loading === 'lazy') img.loading = 'eager';
      }
    });
  }

  /**
   * Moves the main gallery to the photo for the chosen size + tile, if there
   * is one. Already there: nothing, so a correction is a no-op.
   *
   * Always instant, like the gallery swap BM already does on a size change.
   * Horizon animates a jump of more than one slide by reordering the slides
   * and refusing every select until it lands, so an animated move let a
   * second quick pick lose the race and leave the wrong photo up.
   * @returns {boolean} whether the gallery was asked to move
   */
  #showChosenPhoto() {
    const tile = this.querySelector('input.addon-card__input:checked')?.closest('[data-match-text]');
    const slideshow = this.#gallerySlideshow();
    if (!(tile instanceof HTMLElement) || !slideshow) return false;

    const slides = slideshow.slides ?? [];
    const tagged = slides.filter((slide) => slide.dataset.addonPhoto);
    if (!tagged.length) return false;

    // Every tile of the chosen tile's size, sold out included: the ones the
    // size filter shows (every tile when there is no filter option).
    const sizeBoxes = [];
    for (const option of this.querySelectorAll('[data-match-text]')) {
      if (!(option instanceof HTMLElement)) continue;
      const wrapper = option.closest('.resource-list__item, .resource-list__slide') || option;
      if (!wrapper.hasAttribute('hidden')) sizeBoxes.push(slugify(option.dataset.matchText));
    }

    const best = photoFor(
      tagged.map((slide) => slide.dataset.addonPhoto || ''),
      slugify(tile.dataset.matchText),
      this.dataset.matchOptionPosition ? slugify(this.#matchValue) : null,
      sizeBoxes
    );
    if (best < 0) return false;

    const slide = tagged[best];
    showPhotoLoading(slide);
    // Compared by element: while a shopper's own animated jump is landing,
    // the slideshow has its slides reordered.
    if (!slides.includes(slide) || slides[slideshow.current] === slide) return false;
    slideshow.select(slides.indexOf(slide), undefined, { animate: false });
    return true;
  }

  /**
   * Moves to the chosen photo and keeps it in place while the gallery settles:
   *   - after a variant change the gallery arrives on the variant's own image,
   *     and Horizon's slideshow may select a slide of its own a frame later
   *     (its setup re-selects slide 0 when it finds none in view), or may not
   *     be ready to select yet;
   *   - a select made while a shopper's animated jump is landing is refused.
   * So every frame of the hold re-checks, instantly and as a no-op once the
   * photo is right. It ends PHOTO_HOLD_MS in once the photo is right, and at
   * PHOTO_HOLD_MAX_MS regardless; the shopper touching, scrolling or keying
   * the gallery ends it at once.
   *
   * Once per frame, never from a slideshow select event: two correctors
   * answering each other's selects in microtasks never yield to a frame or a
   * timer and lock the page. One hold per page (a new one ends any other
   * picker's), so the worst case is a repeat per frame until the cap.
   * @returns {boolean} whether the gallery was asked to move
   */
  #holdChosenPhoto() {
    activePhotoHold?.abort();
    clearTimeout(this.#photoHoldTimer);

    const hold = new AbortController();
    activePhotoHold = hold;
    this.#photoHold = hold;
    const startedAt = performance.now();

    // On the document, so it also sees a gallery a variant change swaps in
    // mid-hold; capture, so a handler inside the gallery can't hide it.
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
      document.addEventListener(
        type,
        (event) => {
          const gallery = this.#gallerySlideshow()?.closest('media-gallery');
          if (gallery && event.target instanceof Node && gallery.contains(event.target)) hold.abort();
        },
        { capture: true, passive: true, signal: hold.signal }
      );
    }

    const everyFrame = () => {
      if (hold.signal.aborted) return;
      const stillWrong = this.#showChosenPhoto();
      if (!stillWrong && performance.now() - startedAt >= PHOTO_HOLD_MS) {
        hold.abort();
        return;
      }
      requestAnimationFrame(everyFrame);
    };
    requestAnimationFrame(everyFrame);
    this.#photoHoldTimer = setTimeout(() => hold.abort(), PHOTO_HOLD_MAX_MS);

    return this.#showChosenPhoto();
  }

  /**
   * Pressed feedback on a newly chosen tile while the gallery follows it: the
   * tile spins, the others fade and ignore taps, so rapid clicking cannot
   * thrash the gallery. Styled in blocks/addon-variant-picker.liquid.
   * @param {HTMLInputElement} target
   */
  #startSettling(target) {
    this.#finishSettling();
    this.#settlingCard = target.closest('.addon-card');
    this.#settlingCard?.setAttribute('aria-busy', 'true');
    this.classList.add(PHOTO_SETTLING_CLASS);
    this.#settleTimer = setTimeout(() => this.#finishSettling(), PHOTO_SETTLE_MS);
  }

  #finishSettling() {
    clearTimeout(this.#settleTimer);
    this.classList.remove(PHOTO_SETTLING_CLASS);
    this.#settlingCard?.removeAttribute('aria-busy');
    this.#settlingCard = null;
  }

  /** @returns {HTMLInputElement[]} */
  get selectedInputs() {
    return Array.from(this.querySelectorAll('input.addon-card__input:checked'));
  }

  /**
   * GROUP MEMBERSHIP AND PRESENTATION ARE SEPARATE STAMPS.
   *
   * Every add-on chosen on a product page — companion or not — carries
   * `_addon_for` (the main product) and `_bundle` (the exact bouquet LINE). That
   * pair is what makes the add-on part of its bouquet's group: the V1 cart
   * renders the group together, and cart-companion-addons.js removes the whole
   * group when the bouquet goes, so nothing is left orphaned in the cart.
   *
   * `_addon_prop` is a SEPARATE, presentation-only stamp, set only for pickers
   * that mirror a main-item property (Card / Vase / Hatbox). It is what tells
   * the cart to collapse the line into a chip on the bouquet row instead of
   * giving it a row of its own — see snippets/cart-products-custom.liquid.
   *
   * These used to be conflated: independent add-ons (paid gifts) were left
   * entirely unstamped to keep their own row, which also excluded them from the
   * group — so deleting a bouquet stranded the chocolates bought with it. Paid
   * gifts now join the group (they still get their own row, because they carry
   * no `_addon_prop`) and leave with it.
   * @returns {Array<{id: number, quantity: number, properties?: Record<string, string>}>}
   */
  get selectedItems() {
    const pickerIsCompanion = this.dataset.addonCompanion === 'true';
    return this.selectedInputs
      .map((input) => {
        /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
        const item = { id: Number(input.value), quantity: 1 };
        const isCompanion = pickerIsCompanion || input.dataset.addonCompanion === 'true';
        // V1 stamps companions only, so independent add-ons keep their own
        // unlinked cart line. V2 stamps everything, which is what lets a bouquet
        // take its gifts with it when it leaves.
        if (this.dataset.productId && (isCompanion || groupedCartActive())) {
          item.properties = { _addon_for: this.dataset.productId };
          // Presentation stamp — chips, not rows. Only property-mirroring pickers.
          if (isCompanion && this.dataset.propertyName) {
            item.properties._addon_prop = this.dataset.propertyName;
          }
          // Link this add-on to the exact bouquet line it's added with.
          const bundle = currentBundleId(this.dataset.productId);
          if (bundle) item.properties._bundle = bundle;
        }
        return item;
      })
      .filter((item) => item.id > 0);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.classList.contains('addon-card__input')) return;

    // A toggle while a previous immediate request is still settling would
    // desync the tile from the cart — undo the stray flip and let the in-flight
    // request finish.
    if (this.dataset.addonImmediate === 'true' && target.dataset.immediateBusy === 'true') {
      target.checked = !target.checked;
      return;
    }

    // Required single-select pickers (e.g. the hatbox colour) must always keep
    // one tile selected — a hatbox product needs a colour. Re-clicking the
    // already-selected tile would otherwise uncheck it (native checkbox toggle)
    // and leave the product with no colour, so revert that deselect and hold
    // the selection. Switching to a DIFFERENT tile still works: that change
    // fires with the new tile checked, handled by the single-select block below.
    if (this.dataset.addonRequired === 'true' && !target.checked) {
      target.checked = true;
      return;
    }

    // data-addon-mode="single" turns checkbox tiles into a single-select:
    // picking one unchecks the rest. Re-clicking deselects, unless the picker is
    // required (handled above), in which case one tile always stays selected.
    if (this.dataset.addonMode === 'single' && target.checked) {
      for (const input of this.querySelectorAll('input.addon-card__input:checked')) {
        if (input !== target) input.checked = false;
      }
    }

    if (this.dataset.propertyName) this.#syncLineItemProperty();

    // A tile the shopper just picked moves the main photo, with pressed
    // feedback only when the gallery actually moves: no matching photo (every
    // untagged product), the photo already showing, or re-picking the current
    // tile (required pickers revert it above) show no spinner for work that is
    // not happening. A sold-out tile is disabled and never fires change.
    if (this.#followsPhoto && event.isTrusted && target.checked) {
      if (this.#holdChosenPhoto()) this.#startSettling(target);
    }

    // Immediate pickers (cards + gifts): the tile's new state is already toggled,
    // so a checked tile means "add", unchecked means "remove".
    if (this.dataset.addonImmediate === 'true') this.#handleImmediateChange(target);
  }

  /**
   * Immediate-picker change handler. For single-select pickers (radio tiles like
   * the message cards, or data-addon-mode="single") switching to a new tile must
   * also remove the previously-added line — radios never fire a change for the
   * tile that gets deselected — so any sibling still holding a cart line key is
   * removed before the newly selected one is added. Multi-select (gifts) just
   * toggles the one tile.
   * @param {HTMLInputElement} target
   */
  async #handleImmediateChange(target) {
    const singleSelect = target.type === 'radio' || this.dataset.addonMode === 'single';

    if (singleSelect && target.checked) {
      const stale = Array.from(this.querySelectorAll('input.addon-card__input')).filter(
        (input) => input !== target && input instanceof HTMLInputElement && input.dataset.cartLineKey
      );
      for (const input of stale) {
        if (input instanceof HTMLInputElement) await this.#removeSiblingLine(input);
      }
    }

    await this.#toggleImmediateItem(target);
  }

  /**
   * Removes a single-select sibling's cart line when the selection changes. The
   * sibling is already unchecked, so (unlike #toggleImmediateItem) this never
   * re-checks it on error.
   * @param {HTMLInputElement} input
   */
  async #removeSiblingLine(input) {
    if (!input.dataset.cartLineKey) return;
    const label = input.closest('label');
    input.dataset.immediateBusy = 'true';
    label?.setAttribute('aria-busy', 'true');
    try {
      await this.#removeImmediateItem(input);
    } catch (error) {
      console.error('[product-addons] Failed to remove previous selection:', error);
    } finally {
      delete input.dataset.immediateBusy;
      label?.removeAttribute('aria-busy');
    }
  };

  /**
   * Adds (on select) or removes (on deselect) a single immediate-picker tile
   * from the cart right away. Locks the tile while the request is in flight and
   * reverts the optimistic toggle if it fails.
   * @param {HTMLInputElement} input
   */
  async #toggleImmediateItem(input) {
    const label = input.closest('label');
    const wasChecked = input.checked;

    input.dataset.immediateBusy = 'true';
    label?.setAttribute('aria-busy', 'true');

    try {
      if (wasChecked) {
        await this.#addImmediateItem(input);
        this.#flashAdded(label);
      } else {
        await this.#removeImmediateItem(input);
      }
    } catch (error) {
      console.error('[product-addons] Immediate cart update failed:', error);
      input.checked = !wasChecked;
      window.BloomToast?.show('Something went wrong. Please try again.', { type: 'error' });
    } finally {
      delete input.dataset.immediateBusy;
      label?.removeAttribute('aria-busy');
    }
  }

  /**
   * Plays a brief "added" confirmation on the tile: a pop + a checkmark badge
   * that scales in then fades out (CSS-driven via the .addon-card--just-added
   * class and an injected badge element). Transient — no persistent control.
   * @param {Element | null} label
   */
  #flashAdded(label) {
    if (!label) return;
    label.querySelector('.addon-card__feedback')?.remove();
    const badge = document.createElement('span');
    badge.className = 'addon-card__feedback';
    badge.setAttribute('aria-hidden', 'true');
    badge.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    label.appendChild(badge);
    label.classList.add('addon-card--just-added');
    setTimeout(() => {
      label.classList.remove('addon-card--just-added');
      badge.remove();
    }, 1000);
  }

  /**
   * POSTs a single add to /cart/add, stores the returned line key on the input
   * for later removal, then refreshes the cart UI (no drawer).
   * @param {HTMLInputElement} input
   */
  async #addImmediateItem(input) {
    const variantId = Number(input.value);
    if (!(variantId > 0)) return;

    /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
    const item = { id: variantId, quantity: 1 };
    // Same split as the combined-add path in `selectedItems`: every add-on joins
    // the bouquet's group (`_addon_for` + `_bundle`) so it is grouped and removed
    // with it, while `_addon_prop` is set only for property-mirroring pickers and
    // is purely what makes the line render as a chip rather than its own row.
    const isCompanion = this.dataset.addonCompanion === 'true' || input.dataset.addonCompanion === 'true';
    if (this.dataset.productId && (isCompanion || groupedCartActive())) {
      item.properties = { _addon_for: this.dataset.productId };
      if (isCompanion && this.dataset.propertyName) {
        item.properties._addon_prop = this.dataset.propertyName;
      }
      const bundle = currentBundleId(this.dataset.productId);
      if (bundle) item.properties._bundle = bundle;
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: gatherCartSectionIds() }),
    });
    const result = await response.json();
    if (result.status) throw new Error(result.description || result.message || 'Add to cart failed');

    const added = Array.isArray(result.items)
      ? result.items.find((item) => Number(item.variant_id) === variantId)
      : null;
    if (added?.key) input.dataset.cartLineKey = added.key;

    const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());
    dispatchImmediateCartUpdate(this, cart, 'add', result.sections);
  }

  /**
   * Removes the tracked cart line (data-cart-line-key) via /cart/change with
   * quantity 0, then refreshes the cart UI (no drawer). A missing key means
   * there's nothing in the cart to remove (e.g. cleared elsewhere) — no-op.
   * @param {HTMLInputElement} input
   */
  async #removeImmediateItem(input) {
    const key = input.dataset.cartLineKey;
    if (!key) return;

    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0, sections: gatherCartSectionIds() }),
    });
    // /cart/change returns the cart itself (or {status} on error).
    const cart = await response.json();
    if (cart.status) throw new Error(cart.description || cart.message || 'Remove from cart failed');

    delete input.dataset.cartLineKey;
    dispatchImmediateCartUpdate(this, cart, 'remove', cart.sections);
  }

  /**
   * Creates/updates a hidden `properties[<name>]` input inside the main
   * product form so the selected add-on title rides along on the main line item.
   */
  #syncLineItemProperty() {
    const propertyName = this.dataset.propertyName;
    const productId = this.dataset.productId;
    if (!propertyName || !productId) return;

    const form = findMainProductForm(productId)?.querySelector('form');
    if (!form) return;

    const selected = this.selectedInputs[0];
    let input = /** @type {HTMLInputElement | null} */ (
      form.querySelector(`input[data-addon-property="${CSS.escape(propertyName)}"]`)
    );

    if (!selected) {
      input?.remove();
      this.#syncCardFallback(form);
      return;
    }

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${propertyName}]`;
      input.dataset.addonProperty = propertyName;
      form.appendChild(input);
    }

    input.value = selected.dataset.addonTitle || '';

    this.#syncCardFallback(form);
  }

  /**
   * Mirrors the picker's free-card tile onto the main line item as hidden
   * _card_free_id / _card_free_title properties — ALWAYS, as long as a free
   * tile exists, not only when a paid card is selected. cart-companion-addons.js
   * treats any bouquet carrying both `properties[Card]` and `_card_free_id` (and
   * no paid card of its own) as a free-card "needer" and adds ONE shared free
   * card companion line — the image + FREE row — capped at one per order.
   *
   * Keeping the stamp for the DEFAULT free selection (the preselected free tile,
   * which is an immediate picker and so never gets added on click) is what makes
   * the free card render as that companion row instead of only the plain
   * `properties[Card]` text. When a paid card is selected the paid companion
   * still wins (the bouquet is excluded from free-needers), and removing it falls
   * back to this stamp — unchanged. Only dropped when there is no free tile.
   * @param {HTMLFormElement} form
   */
  #syncCardFallback(form) {
    if (this.dataset.propertyName !== 'Card' || this.dataset.addonCompanion !== 'true') return;

    const freeTile = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input.addon-card__input[data-addon-companion="true"]')
    );
    const needsFallback = freeTile != null;

    const existing = form.querySelectorAll('input[data-addon-card-fallback]');
    if (!needsFallback) {
      for (const input of existing) input.remove();
      return;
    }

    const values = { _card_free_id: freeTile.value, _card_free_title: freeTile.dataset.addonTitle || '' };
    for (const [name, value] of Object.entries(values)) {
      let input = /** @type {HTMLInputElement | null} */ (
        form.querySelector(`input[data-addon-card-fallback][name="properties[${name}]"]`)
      );
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.dataset.addonCardFallback = 'true';
        form.appendChild(input);
      }
      input.value = value;
    }
  }
}

if (!customElements.get('product-addons')) {
  customElements.define('product-addons', ProductAddons);
}

// ---------------------------------------------------------------------------
// Single-request add to cart
// ---------------------------------------------------------------------------

/**
 * Form components with a combined add request in flight — blocks repeat
 * submits until the request settles.
 * @type {WeakSet<Element>}
 */
const addInFlight = new WeakSet();

/**
 * Stores a toast for the page we're about to navigate to — the global toast
 * snippet (toast-notifications.liquid) shows it right after the redirect.
 * Needed because in redirect mode the add never dispatches a cart event on a
 * page the customer still sees.
 * @param {string} message
 * @param {{type?: string, description?: string}} [options]
 */
function queueRedirectToast(message, options) {
  try {
    sessionStorage.setItem('bloom:pending-toast', JSON.stringify({ message, ...options }));
  } catch (error) {
    /* sessionStorage unavailable — the toast is best-effort */
  }
}

/**
 * Collects the selected add-on items across every picker for a product.
 * @param {string} productId
 * @returns {Array<{id: number, quantity: number}>}
 */
function collectSelectedAddonItems(productId) {
  const items = [];
  for (const picker of document.querySelectorAll(`product-addons[data-product-id="${productId}"]`)) {
    if (!(picker instanceof ProductAddons)) continue;
    // Immediate pickers (gifts) add/remove on click, so their selections are
    // already in the cart — leaving them out keeps Send Blooms from duplicating them.
    if (picker.dataset.addonImmediate === 'true') continue;
    items.push(...picker.selectedItems);
  }
  return items;
}

/**
 * Section ids of every cart-items component on the page (cart page + drawer),
 * so an add/change request returns their re-rendered HTML to morph in place.
 * @returns {string}
 */
function gatherCartSectionIds() {
  const ids = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
  }
  return ids.join(',');
}

/**
 * Dispatches the cart-update for an immediate add/remove so the cart icon and
 * toast refresh, while keeping the drawer shut. The drawer opens on any
 * cart:update when it carries auto-open (cart-drawer.js); the cart:update
 * handler runs synchronously during dispatch, so stripping auto-open across the
 * dispatch and restoring it on the next frame suppresses the pop without
 * affecting the normal Send Blooms flow.
 * @param {HTMLElement} picker
 * @param {{item_count?: number}} cart
 * @param {'add' | 'remove'} action
 * @param {Record<string, string>} [sections]
 */
function dispatchImmediateCartUpdate(picker, cart, action, sections) {
  const drawers = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]')).map((drawer) => ({
    drawer,
    value: drawer.getAttribute('auto-open'),
  }));
  for (const { drawer } of drawers) drawer.removeAttribute('auto-open');

  try {
    picker.dispatchEvent(
      new CartUpdateEvent(cart, picker.id || 'product-addons-immediate', {
        // Not 'product-form-component': the cart icon then reads itemCount as
        // the absolute count, which item_count is.
        source: 'product-addons-immediate',
        action,
        itemCount: cart.item_count,
        sections,
      })
    );
  } finally {
    requestAnimationFrame(() => {
      for (const { drawer, value } of drawers) drawer.setAttribute('auto-open', value ?? '');
    });
  }
}

/**
 * Toggles the loading spinner on every add-to-cart button inside the form
 * component (styled via [aria-busy] in bloom-custom.css).
 * @param {Element} component
 * @param {boolean} loading
 */
function setAddToCartLoading(component, loading) {
  for (const button of component.querySelectorAll('add-to-cart-component [ref="addToCartButton"]')) {
    if (loading) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }
}

/**
 * Plays the "Added" checkmark on the form's puppet buttons — called once the
 * cart request actually succeeded instead of optimistically on click.
 * @param {Element} component
 */
function animateAdded(component) {
  for (const container of component.querySelectorAll('add-to-cart-component')) {
    const button = container.querySelector('[ref="addToCartButton"]');
    if (button instanceof HTMLElement && button.dataset.puppet === 'true') {
      /** @type {any} */ (container).animateAddToCart?.();
    }
  }
}

/**
 * Marks the main form's add-to-cart buttons as puppets so the stock
 * AddToCartComponent.handleClick skips its optimistic "Added" animation —
 * feedback waits for the server response instead. Re-applied after every
 * variant update because the button can be morphed from server HTML that
 * doesn't carry the attribute.
 */
function suppressOptimisticAddAnimation() {
  const productIds = new Set();
  for (const picker of document.querySelectorAll('product-addons')) {
    if (picker instanceof HTMLElement && picker.dataset.productId) productIds.add(picker.dataset.productId);
  }
  for (const productId of productIds) {
    const component = findMainProductForm(productId);
    if (!component) continue;
    for (const button of component.querySelectorAll('add-to-cart-component [ref="addToCartButton"]')) {
      if (button instanceof HTMLElement) button.dataset.puppet = 'true';
    }
  }
}

suppressOptimisticAddAnimation();
document.addEventListener(ThemeEvents.variantUpdate, () => setTimeout(suppressOptimisticAddAnimation));

// ---------------------------------------------------------------------------
// Variant-change loading state
// ---------------------------------------------------------------------------

// Picking an option in the steps-style variant picker kicks off a fetch +
// morph of the re-rendered section (a size change swaps the colour swatches),
// and match-option pickers (hatbox colours) re-filter only once that same
// response lands — until then nothing moves on screen. Mirror the cart page's
// in-flight treatment: block further clicks and dim the affected pickers
// (.variant-updating, styled in variant-main-picker.liquid and
// addon-variant-picker.liquid).

const VARIANT_UPDATING_CLASS = 'variant-updating';
let variantUpdatingTimer = 0;

function clearVariantUpdating() {
  clearTimeout(variantUpdatingTimer);
  for (const element of document.querySelectorAll(`.${VARIANT_UPDATING_CLASS}`)) {
    element.classList.remove(VARIANT_UPDATING_CLASS);
    element.removeAttribute('aria-busy');
  }
}

document.addEventListener(ThemeEvents.variantSelected, (event) => {
  const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
  // Steps picker on the PDP only — product-card swatches swap images locally
  // and don't want a spinner.
  if (!picker || !picker.querySelector('.variant-option--steps')) return;

  /** @type {Element[]} */
  const targets = [picker, ...document.querySelectorAll('product-addons[data-match-option-position]')];
  for (const element of targets) {
    element.classList.add(VARIANT_UPDATING_CLASS);
    element.setAttribute('aria-busy', 'true');
  }

  // Safety net: a failed fetch never dispatches variant:update, which would
  // otherwise leave the picker locked.
  clearTimeout(variantUpdatingTimer);
  variantUpdatingTimer = setTimeout(clearVariantUpdating, 10000);
});

// The morph rebuilds the picker from server HTML (dropping the class there),
// but the addon pickers outside the morphed tree need the explicit clear.
document.addEventListener(ThemeEvents.variantUpdate, clearVariantUpdating);

/**
 * Shows a server error message in the stock error slot (ref=addToCartTextError)
 * and announces it, mirroring ProductFormComponent's error display.
 * @param {Element} component
 * @param {string} message
 */
function showAddToCartError(component, message) {
  const refs = /** @type {any} */ (component).refs;
  const errorElement = refs?.addToCartTextError;
  const liveRegion = refs?.liveRegion;
  if (!(errorElement instanceof HTMLElement)) return;

  errorElement.classList.remove('hidden');
  const textNode = errorElement.childNodes[2];
  if (textNode) textNode.textContent = message;
  else errorElement.appendChild(document.createTextNode(message));

  if (liveRegion instanceof HTMLElement) liveRegion.textContent = message;

  setTimeout(() => {
    errorElement.classList.add('hidden');
    if (liveRegion instanceof HTMLElement) liveRegion.textContent = '';
  }, 10000);
}

/**
 * Adds the main product and every selected add-on in ONE /cart/add request,
 * then redirects or dispatches a single cart update so the cart icon, drawer
 * and cart page refresh once, with everything already in the cart.
 * @param {HTMLElement} component
 * @param {HTMLFormElement} form
 * @param {Array<{id: number, quantity: number}>} addonItems
 */
async function addAllToCart(component, form, addonItems) {
  const productId = String(component.dataset.productId);
  const redirectUrl = component.dataset.cartRedirect;
  let redirecting = false;

  addInFlight.add(component);
  setAddToCartLoading(component, true);

  const formData = new FormData(form);

  // Same intent resolution as the stock handler: while a variant change is in
  // flight the hidden id input may lag, but the URL param already holds the
  // variant the customer picked.
  const urlVariantId = new URL(window.location.href).searchParams.get('variant');

  /** @type {{id: number, quantity: number, properties?: Record<string, string>, selling_plan?: number}} */
  const mainItem = {
    id: Number(urlVariantId || formData.get('id')),
    quantity: Number(formData.get('quantity')) || Number(component.dataset.quantityDefault) || 1,
  };

  /** @type {Record<string, string>} */
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const name = key.match(/^properties\[(.+)\]$/)?.[1];
    if (name && typeof value === 'string') properties[name] = value;
  }
  if (Object.keys(properties).length > 0) mainItem.properties = properties;

  const sellingPlan = formData.get('selling_plan');
  if (typeof sellingPlan === 'string' && sellingPlan !== '') mainItem.selling_plan = Number(sellingPlan);

  const sectionIds = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [mainItem, ...addonItems], sections: sectionIds.join(',') }),
    });
    const result = await response.json();

    if (result.status) {
      // Multi-item adds fail atomically — nothing was added, so surface the
      // server message where the stock handler would and let the customer retry.
      showAddToCartError(component, result.message);
      component.dispatchEvent(
        new CartErrorEvent(form.getAttribute('id') || '', result.message, result.description, result.errors)
      );
      return;
    }

    if (redirectUrl) {
      // Keep the spinner and in-flight lock up while the cart page loads.
      redirecting = true;
      queueRedirectToast('Added to cart', { type: 'success' });
      window.location.assign(redirectUrl);
      return;
    }

    const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());

    animateAdded(component);
    // This bouquet keeps the bundle id it was stamped with; the next add on
    // this page starts a fresh bundle so two adds never merge into one line.
    rotateBundleId(productId);
    component.dispatchEvent(
      new CartAddEvent(cart, component.id, {
        // Anything but 'product-form-component' here: the cart icon treats
        // that source as an increment and every other source as the absolute
        // cart count, which item_count is.
        source: 'product-addons',
        itemCount: cart.item_count,
        productId,
        sections: result.sections,
      })
    );
  } catch (error) {
    console.error('[product-addons] Combined add to cart failed:', error);
  } finally {
    if (!redirecting) {
      setAddToCartLoading(component, false);
      addInFlight.delete(component);
    }
  }
}

// Intercepts the main product form's submit. Listening on window in capture
// phase is load-bearing: the theme's on:submit delegation is a document-level
// capture listener, and window capture fires first — stopPropagation() here
// is what keeps ProductFormComponent.handleSubmit (and its separate
// single-item request) from running.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const component = form.closest('product-form-component');
    if (!(component instanceof HTMLElement) || !component.dataset.productId) return;

    const productId = component.dataset.productId;
    if (component !== findMainProductForm(productId)) return;

    if (addInFlight.has(component)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Mirror the stock handler's early-outs so it keeps owning those paths
    // (disabled buttons, max-quantity error message).
    const buttons = Array.from(component.querySelectorAll('add-to-cart-component [ref="addToCartButton"]'));
    if (buttons.some((button) => button instanceof HTMLButtonElement && button.disabled)) return;

    const validation = /** @type {any} */ (component).refs?.quantitySelector?.canAddToCart?.();
    if (validation && !validation.canAdd) return;

    const items = collectSelectedAddonItems(productId);

    if (items.length === 0) {
      // No add-ons selected: the stock single-item flow is already one
      // request — just mirror the loading state until its cart:update lands.
      // Lock the form too so a second submit while the request is in flight is
      // blocked by the addInFlight guard above (double-submit prevention); the
      // cart:update handler clears it. 15s safety net because the stock handler
      // swallows network errors without dispatching any event.
      addInFlight.add(component);
      setAddToCartLoading(component, true);
      setTimeout(() => {
        setAddToCartLoading(component, false);
        addInFlight.delete(component);
      }, 15000);
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    addAllToCart(component, form, items);
  },
  { capture: true }
);

/**
 * FALLBACK: batch-adds the selected add-ons after the main product was added
 * by the stock ProductFormComponent — only reached for adds the submit
 * interception above didn't take over (e.g. queued during a variant change).
 * When redirectUrl is set (buy-buttons "Go to cart page after add to cart"),
 * navigates to the cart page after the batch-add settles — success or not,
 * the main product is already in the cart and the cart page is the required
 * next step (delivery details). Old-theme `return_to: routes.cart_url`
 * behaviour, deferred until the add-ons are in.
 * @param {Array<{id: number, quantity: number}>} items
 * @param {string} productId
 * @param {string} [redirectUrl]
 */
async function addSelectedAddonsToCart(items, productId, redirectUrl) {
  const sectionIds = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sectionIds.push(element.dataset.sectionId);
    }
  }

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const result = await response.json();

    if (result.status) {
      console.error('[product-addons] Failed to add add-ons to cart:', result.message);
      if (redirectUrl) {
        queueRedirectToast('Some add-ons could not be added', { type: 'error' });
        window.location.assign(redirectUrl);
      }
      return;
    }

    if (redirectUrl) {
      queueRedirectToast('Added to cart', { type: 'success' });
      window.location.assign(redirectUrl);
      return;
    }

    const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json());

    document.dispatchEvent(
      new CartAddEvent(cart, 'product-addons', {
        // The cart icon treats non-'product-form-component' sources as the
        // absolute count, so pass the full cart total, not the add-on count.
        source: 'product-addons',
        itemCount: cart.item_count,
        productId,
        sections: result.sections,
      })
    );
  } catch (error) {
    console.error('[product-addons] Failed to add add-ons to cart:', error);
    if (redirectUrl) {
      queueRedirectToast('Some add-ons could not be added', { type: 'error' });
      window.location.assign(redirectUrl);
    }
  }
}

// Companion to the submit interception: reacts to adds that still went
// through the stock ProductFormComponent (no add-ons selected at submit time,
// or an add queued during a variant change). Clears the mirrored loading
// state, plays the deferred "Added" checkmark, batch-adds any selected
// add-ons and handles the cart redirect. One listener for the whole page:
// reacting per-picker would fire one POST per section.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = event.detail?.data;
  if (!data || data.source !== 'product-form-component') return;

  const productId = data.productId;
  if (!productId) return;

  const component = findMainProductForm(String(productId));
  if (component) {
    setAddToCartLoading(component, false);
    addInFlight.delete(component);
  }

  if (data.didError) return;

  const items = collectSelectedAddonItems(String(productId));
  const redirectUrl = component?.dataset.cartRedirect;

  // The add-on items above already captured the current bundle id; start a
  // fresh bundle so a subsequent add on this page can't merge into this one.
  rotateBundleId(String(productId));

  if (items.length === 0) {
    // The stock queue path (variant change in flight) already played the
    // optimistic animation itself; puppet buttons only animate here.
    if (component) animateAdded(component);
    if (redirectUrl) {
      queueRedirectToast('Added to cart', { type: 'success' });
      window.location.assign(redirectUrl);
    }
    return;
  }

  addSelectedAddonsToCart(items, String(productId), redirectUrl);
});

// In redirect mode the customer lands on the cart page right after adding, so
// the cart drawer must not flash open in the meantime. PDP-scoped: this module
// only loads alongside the add-on sections, other pages keep drawer behaviour.
if (document.querySelector('product-form-component[data-cart-redirect]')) {
  for (const drawer of document.querySelectorAll('cart-drawer-component[auto-open]')) {
    drawer.removeAttribute('auto-open');
  }
}

// Reset any in-flight add-to-cart state when the page is restored from the
// browser's back/forward cache (bfcache). In redirect mode the spinner and the
// addInFlight lock are deliberately left up while navigating to the cart page;
// pressing Back restores this frozen PDP snapshot with that state intact, which
// would otherwise leave the button spinning forever AND blocked by the
// addInFlight guard on the next submit. Clearing it makes the button live again.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  for (const button of document.querySelectorAll(
    'add-to-cart-component [ref="addToCartButton"][aria-busy="true"]'
  )) {
    button.removeAttribute('aria-busy');
  }
  for (const component of document.querySelectorAll('product-form-component')) {
    addInFlight.delete(component);
    if (component instanceof HTMLElement && component.dataset.productId) {
      // The bouquet added just before navigating away keeps its bundle id;
      // this restored PDP snapshot must start a fresh one so a re-add here
      // doesn't merge into the line already in the cart.
      rotateBundleId(component.dataset.productId);
    }
  }
  clearVariantUpdating();
});
